import { router } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { CatalogSearchList } from "@/components/editor/CatalogSearchList"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { WorkspaceCategory } from "@/types/bids"
import type { CatalogItem } from "@/types/catalog"

/** Add a catalog product to a level's lineup. The picker's row IS the
 *  snapshot (rooms.product idiom); the server seeds the vendor cost — the
 *  one number the client must never supply. */
export function AddLineupProductDialog({
  open,
  onOpenChange,
  category,
  addUrl,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: WorkspaceCategory
  addUrl: string
}) {
  const [picked, setPicked] = useState<CatalogItem | null>(null)
  const [levelId, setLevelId] = useState("")
  const [builderCode, setBuilderCode] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setPicked(null)
    setBuilderCode("")
    setSubmitting(false)
    setLevelId(category.levels[0] ? String(category.levels[0].id) : "")
  }, [open, category])

  const areaUnit = picked == null || picked.unit === "SF" || picked.unit === "SY"

  const save = () => {
    if (!picked || !levelId) return
    setSubmitting(true)
    router.post(
      addUrl,
      {
        bid_lineup_product: {
          bid_level_id: Number(levelId),
          builder_code: builderCode.trim() || null,
          item: {
            source: "fc_replica",
            id: picked.id,
            style: picked.style,
            color: picked.color,
            product_type: picked.product_type,
            format_label: picked.format_label,
            unit: picked.unit,
            sqft_per_carton: picked.sqft_per_carton,
            vendor_name: picked.vendor_name,
          },
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" data-testid="lineup-picker-dialog">
        <DialogHeader>
          <DialogTitle>Add to the {category.label} lineup</DialogTitle>
          <DialogDescription>
            The vendor cost pulls from the catalog automatically; the level&apos;s quoted price
            covers every product in it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <CatalogSearchList active={open} scope="flooring" picked={picked} onPick={setPicked} />
          {!areaUnit && (
            <div className="callout-signal">
              <p>
                {picked?.unit ?? "This"} pricing can&apos;t carry a per-SF cost — lineup products
                must be SF or SY goods.
              </p>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="lineup-level">Level</label>
              <Select
                id="lineup-level"
                value={levelId}
                onChange={(e) => setLevelId(e.target.value)}
                data-testid="lineup-level-select"
              >
                {category.levels.length === 0 && <option value="">Add a level first…</option>}
                {category.levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="lineup-code">Builder&apos;s item #</label>
              <Input
                id="lineup-code"
                value={builderCode}
                onChange={(e) => setBuilderCode(e.target.value)}
                placeholder="BLD45-166 (optional)"
                data-testid="lineup-builder-code"
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={!picked || !levelId || !areaUnit || submitting}
            data-testid="lineup-save"
          >
            Add product
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
